// components/PrivacyPolicy.jsx
import React from "react";
import { Box, Typography, Paper } from "@mui/material";
import { styled } from "@mui/material/styles";
import { Shield } from "lucide-react";

const Container = styled(Paper)(({ theme }) => ({
  width: "100%",
  margin: "0 auto",
  boxShadow: theme.shadows[6],
  display: "flex",
  flexDirection: "column",
}));

const Header = styled(Box)(({ theme }) => ({
  background: "linear-gradient(90deg, #f97316, #ef4444)",
  color: "#fff",
  padding: theme.spacing(3),
}));

const HeaderRow = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const HeaderIcon = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: "50%",
  backgroundColor: "rgba(255, 255, 255, 0.2)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
}));

const HeaderText = styled(Box)(() => ({
  flex: 1,
}));

const HeaderTitle = styled(Typography)(() => ({
  fontWeight: 700,
  marginBottom: 4,
}));

const HeaderSubtitle = styled(Typography)(() => ({
  color: "rgba(255,255,255,0.85)",
}));

const Content = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
}));

const Section = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 700,
  color: "#1f2937",
  marginBottom: theme.spacing(1.5),
}));

const Paragraph = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  lineHeight: 1.6,
  color: theme.palette.grey[700],
}));

const List = styled("ul")(({ theme }) => ({
  marginLeft: theme.spacing(3),
  marginBottom: theme.spacing(2),
  "& li": {
    marginBottom: theme.spacing(1),
    color: theme.palette.grey[700],
    lineHeight: 1.6,
  },
}));

const HighlightBox = styled(Box)(({ theme }) => ({
  backgroundColor: "#fff7ed",
  borderRadius: theme.spacing(2),
  padding: theme.spacing(2),
  border: "2px solid #fed7aa",
  marginBottom: theme.spacing(2),
}));

const HighlightText = styled(Typography)(() => ({
  color: "#9a3412",
  fontWeight: 600,
  fontSize: "0.95rem",
}));

const LastUpdated = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[500],
  fontStyle: "italic",
  marginBottom: theme.spacing(3),
}));

const Footer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.grey[200]}`,
  backgroundColor: "#f9fafb",
  textAlign: "center",
}));

const FooterText = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
}));

const PrivacyPolicy = () => {
  return (
    <Container>
      <Header>
        <HeaderRow>
          <HeaderIcon>
            <Shield size={24} color="#fff" />
          </HeaderIcon>
          <HeaderText>
            <HeaderTitle variant="h5">Privacy Policy</HeaderTitle>
            <HeaderSubtitle variant="body2">
              Your privacy matters to us
            </HeaderSubtitle>
          </HeaderText>
        </HeaderRow>
      </Header>

      <Content>
        <LastUpdated variant="body2">
          Last Updated:{" "}
          {new Date().toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
          })}
        </LastUpdated>

        <Paragraph>
          Welcome to Foodie Call! We respect your privacy and are committed to
          protecting your information. This Privacy Policy explains how we
          collect, use, and safeguard your data when you use our restaurant
          discovery application.
        </Paragraph>

        <Section>
          <SectionTitle variant="h6">1. Information We Collect</SectionTitle>
          <Paragraph>
            <strong>Location Data:</strong> We collect and store your location
            coordinates in our database (Amazon DynamoDB) to find restaurants
            near you through the Foursquare API. This location data is stored
            permanently but cannot be linked to you personally since we don't
            collect any identifying information.
          </Paragraph>
          <Paragraph>
            <strong>Session Data:</strong> When you create or join a group
            session, we store:
          </Paragraph>
          <List>
            <li>Session codes and participant information</li>
            <li>Restaurant preferences and voting choices</li>
            <li>
              Session settings (search radius, cuisine preferences, price range)
            </li>
            <li>Voting history and match results</li>
          </List>
          <Paragraph>
            Session data remains in our database but is completely
            anonymous—there's no way to connect it to any individual user.
          </Paragraph>
          <Paragraph>
            <strong>Analytics Data:</strong> We use PostHog to collect anonymous
            usage analytics, including:
          </Paragraph>
          <List>
            <li>App features you use and how you interact with them</li>
            <li>Device type and browser information</li>
            <li>General usage patterns and session duration</li>
            <li>Error logs and performance metrics</li>
          </List>

          <HighlightBox>
            <HighlightText>
              ⚡ Important: Foodie Call does not require accounts or login. All
              data we collect is completely anonymous and cannot be tied to your
              identity. We do not collect names, email addresses, phone numbers,
              or any other personally identifiable information.
            </HighlightText>
          </HighlightBox>
        </Section>

        <Section>
          <SectionTitle variant="h6">
            2. How We Use Your Information
          </SectionTitle>
          <Paragraph>We use the collected information to:</Paragraph>
          <List>
            <li>Provide restaurant recommendations based on your location</li>
            <li>Facilitate group voting sessions</li>
            <li>Improve app functionality and user experience</li>
            <li>Identify and fix technical issues</li>
            <li>Understand how users interact with Foodie Call</li>
          </List>
        </Section>

        <Section>
          <SectionTitle variant="h6">
            3. Data Sharing and Third Parties
          </SectionTitle>
          <Paragraph>
            We share limited data with the following third-party services:
          </Paragraph>
          <Paragraph>
            <strong>Foursquare:</strong> We share your location coordinates to
            retrieve restaurant data. Please review{" "}
            <a
              href="https://foursquare.com/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb", fontWeight: 600 }}
            >
              Foursquare's Privacy Policy
            </a>{" "}
            for details on how they handle location data.
          </Paragraph>
          <Paragraph>
            <strong>PostHog:</strong> We share anonymous usage analytics.
            PostHog does not collect personally identifiable information. Review{" "}
            <a
              href="https://posthog.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb", fontWeight: 600 }}
            >
              PostHog's Privacy Policy
            </a>{" "}
            for more information.
          </Paragraph>
          <Paragraph>
            We do not sell, rent, or share your information with other third
            parties for marketing purposes.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle variant="h6">4. Data Storage and Security</SectionTitle>
          <Paragraph>
            We store session and location data permanently in Amazon DynamoDB, a
            secure cloud database service. This includes your location
            coordinates, session preferences, and voting history.
          </Paragraph>
          <Paragraph>
            Because Foodie Call does not use accounts or login credentials, we
            have no way to identify individual users or link sessions to
            specific people. All stored data is completely anonymous. This
            architecture-by-design ensures your privacy.
          </Paragraph>
          <Paragraph>
            We implement reasonable security measures to protect your
            information from unauthorized access, but no internet transmission
            is 100% secure.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle variant="h6">5. Your Rights and Choices</SectionTitle>
          <Paragraph>You have the right to:</Paragraph>
          <List>
            <li>
              Deny location access (note: this will limit app functionality)
            </li>
            <li>Leave a group session at any time</li>
            <li>
              Clear your browser data to remove any locally stored preferences
            </li>
          </List>
          <Paragraph>
            While we store location and session data, it is completely anonymous
            and cannot be linked to you personally. Because we don't collect
            personally identifiable information or use accounts, there is no
            personal profile to access, modify, or delete. You cannot be
            identified from the data we store.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle variant="h6">6. Children's Privacy</SectionTitle>
          <Paragraph>
            Foodie Call is not intended for children under 13. We do not
            knowingly collect information from children under 13. If you believe
            we have inadvertently collected such information, please contact us.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle variant="h6">7. Changes to This Policy</SectionTitle>
          <Paragraph>
            We may update this Privacy Policy from time to time. Changes will be
            reflected with an updated "Last Updated" date at the top of this
            page. Continued use of Foodie Call after changes constitutes
            acceptance of the updated policy.
          </Paragraph>
        </Section>

        <Section>
          <SectionTitle variant="h6">8. Contact Us</SectionTitle>
          <Paragraph>
            If you have questions or concerns about this Privacy Policy or our
            data practices, please contact us through our{" "}
            <a
              href="/feedback"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#2563eb", fontWeight: 600 }}
            >
              feedback form
            </a>
            .
          </Paragraph>
        </Section>
      </Content>

      <Footer>
        <FooterText variant="body2">
          © {new Date().getFullYear()} Foodie Call. All rights reserved.
        </FooterText>
      </Footer>
    </Container>
  );
};

export default PrivacyPolicy;
